import { useEffect, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { ArrowUp, Bot } from "lucide-react";
import { Button } from "@/components/ui/button";
import { ESTIMATE_CHAT_KEYS, useEstimateChatMessagesQuery } from "@/hooks/queries/estimate-chat";
import { toChatErrorMessage, useEstimateChatStream, type EstimateChatUIMessage } from "@/hooks/mutation/estimate-chat";
import { AssistantMarkdown } from "./AssistantMarkdown";
import { ChatThreadSkeleton } from "./ChatThreadSkeleton";
import { ProposalCard } from "./ProposalCard";
import { StreamingAgentTurn } from "./StreamingAgentTurn";
import { UserMessage } from "./UserMessage";

interface ChatThreadProps {
  estimateId: string;
  // The estimate's org; chats are read from it.
  organizationId: string | undefined;
  chatId?: string; //undefined until chat has >=1 message
  readOnly: boolean;
  // Fires when a draft's first message brings a real chat into existence, so the
  // parent can select it and keep the dropdown in sync.
  onChatCreated: (chatId: string) => void;
}

// Stable, so the scroll effect doesn't rerun on every render while no turn is on screen.
const NO_TURN_MESSAGES: EstimateChatUIMessage[] = [];

export function ChatThread({ estimateId, organizationId, chatId, readOnly, onChatCreated }: ChatThreadProps) {
  const queryClient = useQueryClient();
  // isLoading, not isPending: a draft's disabled query stays pending forever.
  const { data: savedMessages = [], isLoading: isLoadingMessages } = useEstimateChatMessagesQuery(estimateId, organizationId, chatId);
  const { streamingTurnMessages, streamingTurnChatId, isTurnInFlight, turnError, canRetry, send, retry } =
    useEstimateChatStream(estimateId);
  // A turn keeps streaming when the user switches chats, but only shows in the chat it was sent to.
  const isTurnOnScreen = streamingTurnChatId !== undefined && streamingTurnChatId === chatId;
  const turnMessages = isTurnOnScreen ? streamingTurnMessages : NO_TURN_MESSAGES;
  const isTurnStreamingHere = isTurnOnScreen && isTurnInFlight;
  const error = isTurnOnScreen ? turnError : undefined;
  // The streaming turn's messages can also be in the cache (a refetch mid-stream, or the saved turn just
  // before useChat clears); show each once, from the streaming turn.
  const turnMessageIds = new Set(turnMessages.map((m) => m.id));
  const messages = savedMessages.filter((m) => !turnMessageIds.has(m.id));

  const [input, setInput] = useState("");
  const listRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = listRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [messages, turnMessages, isTurnStreamingHere]);

  const handleSend = () => {
    const content = input.trim();
    // One turn at a time: the composer stays locked in every chat while any turn is in flight.
    if (!content || isTurnInFlight) return;
    setInput("");
    // A draft has no chatId yet: mint one now so the message creates the chat server-side. Seed
    // its message cache before selecting it, or the query would fetch the not-yet-created chat and 404.
    const id = chatId ?? crypto.randomUUID();
    if (!chatId) queryClient.setQueryData(ESTIMATE_CHAT_KEYS.messages(estimateId, id), []);
    onChatCreated(id);
    send(id, content);
  };

  if (isLoadingMessages) return <ChatThreadSkeleton />;

  const isEmpty = messages.length === 0 && turnMessages.length === 0 && !isTurnStreamingHere;

  return (
    <>
      <div ref={listRef} className="min-h-0 flex-1 overflow-y-auto">
        {isEmpty && !error ? (
          <div className="flex h-full flex-col items-center justify-center gap-2 px-4 text-center">
            <Bot className="size-4 text-muted-foreground" />
            <p className="text-sm font-medium text-balance">Ask the agent about this estimate</p>
          </div>
        ) : (
          <div className="flex flex-col gap-4 px-4 py-4">
            {messages.map((message) =>
              message.role === "user" ? (
                <UserMessage key={message.id} content={message.content} />
              ) : (
                <div key={message.id} className="flex max-w-[95%] flex-col gap-3">
                  <AssistantMarkdown>{message.content}</AssistantMarkdown>
                  {chatId && message.proposals && (
                    <ProposalCard estimateId={estimateId} chatId={chatId} proposals={message.proposals} readOnly={readOnly} />
                  )}
                </div>
              ),
            )}
            {/* "Thinking…" lives in StreamingAgentTurn, which knows when nothing visible has arrived. */}
            <StreamingAgentTurn messages={turnMessages} isStreaming={isTurnStreamingHere} />
            {error && (
              <div className="flex items-center justify-between gap-2 rounded-lg border border-red-300 bg-red-50 px-3 py-2 text-[11px] text-red-900">
                <span>{toChatErrorMessage(error)}</span>
                {!readOnly && canRetry && (
                  <Button variant="outline" size="sm" className="h-6 shrink-0 px-2 text-[11px]" onClick={retry}>
                    Retry
                  </Button>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Another user's chat is view-only: the composer is simply absent. */}
      {!readOnly && (
        <div className="shrink-0 p-3">
          <div className="rounded-2xl bg-muted p-2 focus-within:ring-1 focus-within:ring-ring/40">
            <textarea
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  handleSend();
                }
              }}
              placeholder="Ask about this estimate…"
              className="min-h-9 w-full resize-none border-0 bg-transparent px-2 py-1 text-sm leading-5 outline-none placeholder:text-muted-foreground"
            />
            <div className="flex items-center justify-end px-1 pb-0.5">
              <Button size="icon" className="rounded-full" disabled={!input.trim() || isTurnInFlight} onClick={handleSend}>
                <ArrowUp className="size-4" />
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
